import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { OtherInsuranceEntry } from '../entries';
import { useOtherInsuranceList } from './hooks/useOtherInsuranceList';
import { ConfirmationModal } from '@components';
import { useState } from 'react';

interface IOtherInsuranceListProps {
  insuranceIdLabel: string;
  onDelete: (id: string | undefined) => Promise<void>;
  onAfterDelete?: () => void;
  onSubmitEntry: (index: number) => Promise<boolean>;
  fieldKey: 'primary_other' | 'secondary';
}

export const OtherInsuranceList = ({
  insuranceIdLabel,
  onDelete,
  onAfterDelete,
  onSubmitEntry,
  fieldKey,
}: IOtherInsuranceListProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    fields,
    expandedId,
    getLabel,
    handleAdd,
    handleRemove,
    handleRequestRemove,
    handleCancelRemove,
    handleToggle,
    hasError,
    getOverlapError,
    pendingRemoveIndex,
    getEntryId,
    isAtLimit,
    isOnlyEntry,
    handleSubmitEntry,
    submittingIndex,
  } = useOtherInsuranceList(fieldKey, onSubmitEntry);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {fields.length === 0 && (
        <Typography variant='body2' sx={{ color: 'text.disabled', py: 1 }}>
          No insurances added.
        </Typography>
      )}
      {fields.map((field, index) => (
        <Box key={field.id}>
          {index > 0 && <Divider />}
          <Accordion
            expanded={expandedId === field.id}
            onChange={() => handleToggle(field.id)}
            disableGutters
            elevation={0}
            sx={{
              border: 'none',
              boxShadow: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 'medium',
                  color: hasError(index) ? 'error.main' : 'inherit',
                }}
              >
                {getLabel(index)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1, pb: 2 }}>
              <OtherInsuranceEntry
                index={index}
                onRemove={handleRemove}
                insuranceIdLabel={insuranceIdLabel}
                overlapError={getOverlapError(index)}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={() => handleSubmitEntry(index)}
                  loading={submittingIndex === index}
                  loadingPosition='start'
                  sx={{ color: 'white' }}
                >
                  {submittingIndex === index ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant='text'
                  color='error'
                  onClick={() => handleRequestRemove(index)}
                  disabled={isOnlyEntry || submittingIndex === index}
                  sx={{ px: 0 }}
                >
                  Delete
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      ))}

      <Button
        variant='text'
        startIcon={<Add />}
        onClick={handleAdd}
        disabled={isAtLimit}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add
      </Button>

      <ConfirmationModal
        isOpen={pendingRemoveIndex !== null}
        title='Delete insurance'
        message='Are you sure you want to delete this insurance entry?'
        confirmText='Delete'
        isLoading={isDeleting}
        onConfirm={async () => {
          if (pendingRemoveIndex === null) return;
          const entryId = getEntryId(pendingRemoveIndex);
          if (entryId) {
            setIsDeleting(true);
            try {
              await onDelete(entryId);
              onAfterDelete?.();
              handleCancelRemove();
            } finally {
              setIsDeleting(false);
            }
          } else {
            handleRemove(pendingRemoveIndex);
          }
        }}
        onClose={handleCancelRemove}
      />
    </Box>
  );
};
